import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { useTranslation } from '@/lib/i18n';
import { useNavigate } from 'react-router';
import { useMemo } from 'react';
import type { VendorApplicationListItem } from '@/lib/api-admin/types';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui';
import { VendorApplicationStatusPill } from './VendorApplicationStatusPill';

export const VENDOR_SORTABLE_COLUMNS = new Set(['submittedAt', 'createdAt', 'slaDueAt']);

export interface VendorApplicationsTableProps {
  items: ReadonlyArray<VendorApplicationListItem>;
  sort: string;
  onSortChange: (sort: string) => void;
}

export const parseVendorSort = (sort: string): { id: string; dir: 'asc' | 'desc' } => {
  const parts = sort.split(',');
  const id = parts[0] && parts[0].length > 0 ? parts[0] : 'submittedAt';
  const dir = parts[1] === 'asc' ? 'asc' : 'desc';
  return { id, dir };
};

export const toggleVendorSort = (current: string, columnId: string): string => {
  const { id, dir } = parseVendorSort(current);
  if (id !== columnId) return `${columnId},desc`;
  return `${columnId},${dir === 'desc' ? 'asc' : 'desc'}`;
};

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const VendorApplicationsTable = ({
  items,
  sort,
  onSortChange,
}: VendorApplicationsTableProps) => {
  const { t } = useTranslation('vendors');
  const navigate = useNavigate();
  const { id: activeSortId, dir: activeSortDir } = parseVendorSort(sort);

  const columns = useMemo<ColumnDef<VendorApplicationListItem>[]>(
    () => [
      {
        id: 'vendorName',
        accessorKey: 'vendorName',
        header: () => t('vendors.columns.vendorName'),
        cell: ({ row }) => (
          <span className="block max-w-[180px] truncate" title={row.original.vendorName}>
            {row.original.vendorName}
          </span>
        ),
      },
      {
        id: 'businessRegistrationNoMasked',
        accessorKey: 'businessRegistrationNoMasked',
        header: () => t('vendors.columns.businessRegistrationNo'),
      },
      {
        id: 'representativeName',
        accessorFn: (row) => row.representativeName ?? '—',
        header: () => t('vendors.columns.representativeName'),
      },
      {
        id: 'contactEmail',
        header: () => t('vendors.columns.contactEmail'),
        cell: ({ row }) => (
          <span className="block max-w-[200px] truncate" title={row.original.contactEmail ?? undefined}>
            {row.original.contactEmail ?? '—'}
          </span>
        ),
      },
      {
        id: 'status',
        header: () => t('vendors.columns.status'),
        cell: ({ row }) => (
          <VendorApplicationStatusPill
            status={row.original.status}
            displayStatus={row.original.displayStatus}
            applicationId={row.original.applicationId}
          />
        ),
      },
      {
        id: 'submittedAt',
        accessorFn: (row) => formatDate(row.submittedAt ?? null),
        header: () => t('vendors.columns.submittedAt'),
      },
      {
        id: 'slaDueAt',
        accessorFn: (row) => formatDate(row.slaDueAt ?? null),
        header: () => t('vendors.columns.slaDueAt'),
      },
      {
        id: 'assignedReviewer',
        accessorFn: (row) => row.assignedReviewer?.name ?? t('vendors.list.unassigned'),
        header: () => t('vendors.columns.assignedReviewer'),
      },
    ],
    [t],
  );

  const table = useReactTable({
    data: items as VendorApplicationListItem[],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (items.length === 0) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
        style={{
          background: 'var(--surface-raised)',
          color: 'var(--content-muted)',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <p>{t('vendors.list.empty')}</p>
      </div>
    );
  }

  const goToDetail = (applicationId: string) => navigate(`/vendors/${applicationId}`);

  return (
    <div
      className="overflow-hidden rounded-lg"
      style={{ background: 'var(--surface-raised)', boxShadow: 'var(--shadow-card)' }}
    >
      <Table data-testid="vendor-apps-table">
        <TableHeader style={{ background: 'var(--surface-1)' }}>
          {table.getHeaderGroups().map((group) => (
            <TableRow key={group.id}>
              {group.headers.map((header) => {
                const isSortable = VENDOR_SORTABLE_COLUMNS.has(header.column.id);
                const isActive = activeSortId === header.column.id;
                const ariaSort: 'ascending' | 'descending' | 'none' = isSortable
                  ? isActive
                    ? activeSortDir === 'asc' ? 'ascending' : 'descending'
                    : 'none'
                  : 'none';
                return (
                  <TableHead
                    key={header.id}
                    scope="col"
                    aria-sort={isSortable ? ariaSort : undefined}
                  >
                    {isSortable ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2"
                        onClick={() => onSortChange(toggleVendorSort(sort, header.column.id))}
                        aria-label={t('vendors.list.sortBy', {
                          column: t(`vendors.columns.${header.column.id}`),
                        })}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        <span aria-hidden="true">
                          {isActive ? (activeSortDir === 'asc' ? '↑' : '↓') : ''}
                        </span>
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              role="link"
              tabIndex={0}
              data-testid="vendor-apps-row"
              aria-label={t('vendors.list.openRow', {
                name: row.original.vendorName,
                defaultValue: row.original.vendorName,
              })}
              className="cursor-pointer focus-visible:outline-none focus-visible:ring-2"
              style={{ height: 'var(--density-compact-row-h)' }}
              onClick={() => goToDetail(row.original.applicationId)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  goToDetail(row.original.applicationId);
                }
              }}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(
                    cell.column.columnDef.cell ?? ((ctx) => ctx.getValue() as string),
                    cell.getContext(),
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
